import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { LegalBody } from "@/components/LegalBody";

const SUPPORT_EMAIL = "support@monoepay.com";

const LegalDoc = () => {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    apiClient
      .get(`/legal/${slug}`)
      .then(({ data }) => {
        setDoc(data);
        setState("ready");
      })
      .catch(() => setState("error"));
  }, [slug]);

  useSeo({
    title: doc ? doc.title : "Legal",
    description: doc ? doc.summary : "MONOE Pay legal document.",
    path: `/legal/${slug}`,
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
      <Link to="/legal" data-testid="legal-back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Legal Center
      </Link>

      {state === "loading" && (
        <div className="mt-10 flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {state === "error" && (
        <div className="mt-10 rounded-lg border border-border bg-card p-8">
          <h1 className="font-display text-xl font-semibold">Document not found</h1>
          <p className="mt-2 text-sm text-muted-foreground">The requested legal document could not be located.</p>
        </div>
      )}

      {state === "ready" && doc && (
        <Reveal>
          <article>
            <header className="border-b border-border pb-8">
              <h1 data-testid={`legal-doc-${doc.slug}-header`} className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
                {doc.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono-ui text-xs text-muted-foreground">
                <span>Version {doc.version}</span>
                <span>Effective {doc.effective_date}</span>
                <span>Updated {doc.last_updated}</span>
                <span>Audience: {doc.audience}</span>
                {doc.acceptance_required && (
                  <span className="rounded-full border border-foreground/30 bg-foreground px-2 py-0.5 text-background">
                    Acceptance required
                  </span>
                )}
              </div>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{doc.summary}</p>
            </header>

            <LegalBody body={doc.body} />

            <p className="mt-12 border-t border-border pt-6 text-xs leading-relaxed text-muted-foreground">
              For general, account, payment, or merchant questions, contact{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="legal-doc-support-link" className="underline underline-offset-2">MONOE Customer Support</a>.
              Any formal-notice or regulatory contact details are provided within the relevant document above.
            </p>
          </article>
        </Reveal>
      )}
    </div>
  );
};

export default LegalDoc;
